import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { 
  RefreshCw, TrendingUp, TrendingDown, Globe, AlertTriangle, 
  LogOut, User, Loader2, Zap, Play, BarChart3, Target, 
  DollarSign, Plus, Clock, ShieldAlert, Activity, Brain, History, LineChart, Settings, Download, SlidersHorizontal, Cloud, HelpCircle, ExternalLink, Trash2
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Strategy badge colors (BUY/SHORT only)
const STRATEGY_COLORS = {
  BUY: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  SHORT: "bg-red-500/20 text-red-400 border-red-500/30",
};

// Available columns for the pending trades table with default widths
const AVAILABLE_COLUMNS = [
  { id: 'probability', label: 'Confidence Score', default: false, minWidth: 100, defaultWidth: 120 },
  { id: 'stock', label: 'Stock Name', default: true, minWidth: 120, defaultWidth: 160 },
  { id: 'ranking_score', label: 'Rank Score', default: false, minWidth: 80, defaultWidth: 100 },
  { id: 'start_price', label: 'Entry Price', default: true, minWidth: 90, defaultWidth: 110 },
  { id: 'actual_price', label: 'Actual Price', default: true, minWidth: 90, defaultWidth: 110 },
  { id: 'actual_date', label: 'Actual Date', default: true, minWidth: 140, defaultWidth: 180 },
  { id: 'trade_datetime', label: 'Trade Date', default: true, minWidth: 100, defaultWidth: 120 },
  { id: 'sector', label: 'Industry', default: false, minWidth: 100, defaultWidth: 140 },
  { id: 'strategy', label: 'Trading Signal', default: true, minWidth: 80, defaultWidth: 100 },
  { id: 'eval_date', label: 'Review Date', default: true, minWidth: 100, defaultWidth: 120 },
  { id: 'predicted_price', label: 'Target Price (2-Day)', default: true, minWidth: 100, defaultWidth: 130 },
  { id: 'shares', label: 'Number of Shares', default: false, minWidth: 70, defaultWidth: 90 },
  { id: 'investment', label: 'Investment Amount', default: false, minWidth: 100, defaultWidth: 120 },
  { id: 'gross_pl', label: 'Gross Profit / Loss', default: false, minWidth: 90, defaultWidth: 110 },
  { id: 'commission', label: 'Brokerage Fee', default: false, minWidth: 90, defaultWidth: 110 },
  { id: 'net_pl', label: 'Net Profit / Loss', default: false, minWidth: 90, defaultWidth: 110 },
  { id: 'actions', label: 'Actions', default: true, minWidth: 120, defaultWidth: 150 },
];

// Default column widths
const getDefaultWidths = () => {
  const widths = {};
  AVAILABLE_COLUMNS.forEach(col => {
    widths[col.id] = col.defaultWidth;
  });
  return widths;
};

export default function AIDashboard() {
  const { user, logout } = useAuth();
  
  // Global panel data
  const [globalPanel, setGlobalPanel] = useState(null);
  const [globalLoading, setGlobalLoading] = useState(true);
  
  // Pending trades data (from multiple days)
  const [pendingTrades, setPendingTrades] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(true);
  
  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('ai_dashboard_columns');
    if (saved) return JSON.parse(saved);
    return AVAILABLE_COLUMNS.filter(c => c.default).map(c => c.id);
  });
  
  // Column widths
  const [columnWidths, setColumnWidths] = useState(() => {
    const saved = localStorage.getItem('ai_dashboard_column_widths');
    if (saved) return JSON.parse(saved);
    return getDefaultWidths();
  });
  
  // Column resize state
  const [resizingColumn, setResizingColumn] = useState(null);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartWidth, setResizeStartWidth] = useState(0);
  
  // Column resize handlers
  const handleResizeStart = (e, columnId) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingColumn(columnId);
    setResizeStartX(e.clientX);
    setResizeStartWidth(columnWidths[columnId] || getDefaultWidths()[columnId]);
  };

  const handleResizeMove = useCallback((e) => {
    if (!resizingColumn) return;
    
    const diff = e.clientX - resizeStartX;
    const col = AVAILABLE_COLUMNS.find(c => c.id === resizingColumn);
    const minWidth = col?.minWidth || 80;
    const newWidth = Math.max(minWidth, resizeStartWidth + diff);
    
    setColumnWidths(prev => ({
      ...prev,
      [resizingColumn]: newWidth
    }));
  }, [resizingColumn, resizeStartX, resizeStartWidth]);

  const handleResizeEnd = useCallback(() => {
    if (resizingColumn) {
      // Save to localStorage when resize ends
      localStorage.setItem('ai_dashboard_column_widths', JSON.stringify(columnWidths));
    }
    setResizingColumn(null);
  }, [resizingColumn, columnWidths]);

  // Add global mouse listeners for resize
  useEffect(() => {
    if (resizingColumn) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      
      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [resizingColumn, handleResizeMove, handleResizeEnd]);

  // Reset column widths to default
  const resetColumnWidths = () => {
    const defaultWidths = getDefaultWidths();
    setColumnWidths(defaultWidths);
    localStorage.setItem('ai_dashboard_column_widths', JSON.stringify(defaultWidths));
    toast.success('Column widths reset to default');
  };
  
  // Column settings dialog
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
  
  // Investment calculator
  const [investmentAmount, setInvestmentAmount] = useState(1000000);
  const [commissionPct, setCommissionPct] = useState(0.2);
  
  // Custom stock
  const [addStockOpen, setAddStockOpen] = useState(false);
  const [newStockSymbol, setNewStockSymbol] = useState("");
  const [newStockSector, setNewStockSector] = useState("");
  const [addingStock, setAddingStock] = useState(false);
  
  // Running pipeline
  const [runningPipeline, setRunningPipeline] = useState(false);
  
  // Zerodha integration status
  const [zerodhaStatus, setZerodhaStatus] = useState(null);
  const [zerodhaLoading, setZerodhaLoading] = useState(false);
  
  // Evaluating individual stock
  const [evaluatingStock, setEvaluatingStock] = useState(null);
  
  // Evaluate single stock
  const evaluateStock = async (symbol) => {
    try {
      setEvaluatingStock(symbol);
      const cleanSymbol = symbol.replace('.NS', '').replace('.BO', '');
      const res = await axios.post(`${API}/ai/evaluate-stock/${cleanSymbol}`);
      toast.success(`${cleanSymbol}: ${res.data.evaluation_result} at ₹${res.data.actual_price}`);
      // Refresh the trades list
      fetchPendingTrades();
    } catch (err) {
      console.error("Error evaluating stock:", err);
      toast.error(err.response?.data?.detail || `Failed to evaluate ${symbol}`);
    } finally {
      setEvaluatingStock(null);
    }
  };

  // Delete a stock from dashboard
  const [deletingStock, setDeletingStock] = useState(null);
  
  const deleteStock = async (trade) => {
    // Check if trade has ID
    if (!trade.id) {
      toast.error(`Cannot delete ${trade.symbol.replace('.NS', '').replace('.BO', '')}: No ID available (stock may be from pipeline)`);
      console.error("Trade without ID:", trade);
      return;
    }
    
    // Ask for confirmation
    if (!window.confirm(`Delete ${trade.symbol.replace('.NS', '').replace('.BO', '')} from dashboard?`)) {
      return;
    }
    
    try {
      setDeletingStock(trade.symbol);
      await axios.delete(`${API}/ai/dashboard/stock/${trade.id}`);
      toast.success(`${trade.symbol.replace('.NS', '').replace('.BO', '')} removed from dashboard`);
      // Refresh the trades list
      fetchPendingTrades();
    } catch (err) {
      console.error("Error deleting stock:", err);
      toast.error(err.response?.data?.detail || `Failed to delete ${trade.symbol}`);
    } finally {
      setDeletingStock(null);
    }
  };
  
  // Stock master list for browsing
  const [masterStocks, setMasterStocks] = useState([]);
  const [masterSectors, setMasterSectors] = useState([]);
  const [stockSearchTerm, setStockSearchTerm] = useState("");
  const [selectedSectorFilter, setSelectedSectorFilter] = useState("");

  // Toggle column visibility
  const toggleColumn = (columnId) => {
    setVisibleColumns(prev => 
      prev.includes(columnId) 
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId]
    );
  };

  const isColumnVisible = (columnId) => visibleColumns.includes(columnId);

  // Fetch global market panel - with caching
  const fetchGlobalPanel = useCallback(async () => {
    try {
      // Don't show loading spinner if we already have data (smoother UX)
      if (!globalPanel) setGlobalLoading(true);
      
      const res = await axios.get(`${API}/ai/global-panel`, {
        timeout: 10000 // 10 second timeout
      });
      setGlobalPanel(res.data);
    } catch (err) {
      console.error("Error fetching global panel:", err);
      // Only show error if we don't have cached data
      if (!globalPanel) {
        toast.error("Failed to fetch global market data");
      }
    } finally {
      setGlobalLoading(false);
    }
  }, [globalPanel]);

  // Fetch pending trades (not yet evaluated) - optimized
  const fetchPendingTrades = useCallback(async () => {
    try {
      // Don't show loading spinner if we already have data
      if (pendingTrades.length === 0) setPendingLoading(true);
      
      // Add cache-busting timestamp
      const cacheBuster = Date.now();
      
      // ONLY fetch from pipeline/predictions (daily_predictions collection)
      // This is the single source of truth for dashboard stocks
      const pipelineRes = await axios.get(`${API}/ai/pipeline/predictions?days=7&_t=${cacheBuster}`, { 
        timeout: 15000,
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
      });
      
      const pipelineTrades = pipelineRes.data.trades || [];
      
      // Filter out evaluated stocks
      const unevaluatedTrades = pipelineTrades
        .filter(t => !t.is_evaluated && t.symbol && t.start_price > 0)
        .map(t => ({
          ...t,
          trade_date: t.trade_date || t.date,
          start_price: t.start_price || t.current_price,
          actual_price: t.actual_price || null,
          shares_allocated: t.shares_allocated || t.shares,
          investment_used: t.investment_used || t.investment,
          probability_score: t.probability_score || t.probability || t.confidence,
          evaluation_date: t.evaluation_date,
          ranking_score: t.ranking_score,
          manually_added: t.manually_added,
        }))
        .sort((a, b) => {
          // Sort by trade_date DESC, then ranking_score DESC
          const dateA = a.trade_date || '';
          const dateB = b.trade_date || '';
          if (dateA !== dateB) {
            return dateB.localeCompare(dateA);
          }
          return (b.ranking_score || 0) - (a.ranking_score || 0);
        });
      
      setPendingTrades(unevaluatedTrades);
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error("Error fetching pending trades:", err);
      }
      if (pendingTrades.length === 0) setPendingTrades([]);
    } finally {
      setPendingLoading(false);
    }
  }, [pendingTrades.length]);

  // Fetch Zerodha status
  const fetchZerodhaStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/zerodha/status`, { timeout: 5000 });
      setZerodhaStatus(res.data);
    } catch (err) {
      console.error("Error fetching Zerodha status:", err);
      setZerodhaStatus({ status: 'unavailable' });
    }
  }, []);

  // Handle Zerodha login
  const handleZerodhaLogin = async () => {
    try {
      setZerodhaLoading(true);
      const res = await axios.get(`${API}/zerodha/login-url`);
      const loginUrl = res.data.login_url;
      
      // Open Zerodha login in new window
      window.open(loginUrl, '_blank', 'width=600,height=700');
      
      toast.info("Complete login in Zerodha window. This page will refresh when done.");
      
      // Poll for authentication status
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/zerodha/status`);
          if (statusRes.data.authenticated) {
            clearInterval(pollInterval);
            setZerodhaStatus(statusRes.data);
            toast.success("Zerodha login successful!");
            setZerodhaLoading(false);
          }
        } catch (e) {
          // Ignore polling errors
        }
      }, 3000);
      
      // Stop polling after 5 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        setZerodhaLoading(false);
      }, 300000);
      
    } catch (err) {
      console.error("Error initiating Zerodha login:", err);
      toast.error("Failed to initiate Zerodha login");
      setZerodhaLoading(false);
    }
  };

  // Run AI pipeline manually - optimized
  const runPipeline = async () => {
    try {
      setRunningPipeline(true);
      toast.info("AI pipeline started. Refresh in 30-60 seconds to see results.", { duration: 30000 });
      
      await axios.post(`${API}/ai/run-daily`, {
        total_investment: investmentAmount,
        commission_pct: commissionPct,
      }, { timeout: 30000 });
      
      toast.success("Pipeline started! Results will appear shortly.");
      
      // Auto-refresh after 45 seconds
      setTimeout(() => {
        fetchPendingTrades();
        fetchGlobalPanel();
      }, 45000);
      
    } catch (err) {
      console.error("Error running pipeline:", err);
      toast.error(`Pipeline failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setRunningPipeline(false);
    }
  };

  // Add custom stock with full evaluation
  const handleAddStock = async () => {
    if (!newStockSymbol.trim()) {
      toast.error("Please enter a stock symbol");
      return;
    }
    
    try {
      setAddingStock(true);
      const symbol = newStockSymbol.trim().toUpperCase();
      
      toast.info(`Evaluating ${symbol} with all 14 modules...`, { duration: 10000 });
      
      const res = await axios.post(`${API}/ai/stocks/add-with-evaluation`, { 
        symbol,
        sector: newStockSector || "Other",
        force_add: true
      });
      
      if (res.data.status === "success") {
        const pred = res.data.prediction;
        toast.success(
          `Added ${symbol}: ${pred.strategy} | Score: ${pred.ranking_score} | Confidence: ${pred.confidence}%`,
          { duration: 5000 }
        );
        
        // Refresh pending trades
        fetchPendingTrades();
      } else if (res.data.status === "exists") {
        toast.info(`${symbol} already in today's predictions`);
      }
      
      setNewStockSymbol("");
      setNewStockSector("");
      setAddStockOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add stock");
    } finally {
      setAddingStock(false);
    }
  };

  // Calculate summary stats from pending trades
  const calculateSummary = useCallback(() => {
    if (pendingTrades.length === 0) {
      return {
        totalTrades: 0,
        tradeableCount: 0,
        buyCount: 0,
        shortCount: 0,
        totalInvested: 0,
        totalGrossProfit: 0,
        totalCommission: 0,
        totalNetProfit: 0,
        unusedCash: investmentAmount,
      };
    }
    
    // Calculate allocation for ALL stocks
    const allocationPerStock = investmentAmount / pendingTrades.length;
    let totalInvested = 0;
    let totalGrossProfit = 0;
    let buyCount = 0;
    let shortCount = 0;
    
    pendingTrades.forEach(t => {
      // Use strategy from database - don't recalculate
      const displayStrategy = t.strategy || 'BUY';
      const startPrice = t.start_price;
      const predictedPrice = t.predicted_price;  // Keep null as null
      
      // Skip stocks without valid start price
      if (!startPrice || startPrice === 0) {
        return;
      }
      
      if (displayStrategy === 'BUY') buyCount++;
      else shortCount++;
      
      const shares = Math.floor(allocationPerStock / startPrice);
      const invested = shares * startPrice;
      totalInvested += invested;
      
      // Gross Profit: Only calculate if predicted price exists
      // BUY = (predicted - start) × shares, SHORT = (start - predicted) × shares
      if (predictedPrice !== null && predictedPrice !== undefined) {
        const priceDiff = displayStrategy === 'BUY' 
          ? predictedPrice - startPrice
          : startPrice - predictedPrice;
        totalGrossProfit += priceDiff * shares;
      }
    });
    
    const totalCommission = totalInvested * (commissionPct / 100);
    
    return {
      totalTrades: pendingTrades.length,
      tradeableCount: pendingTrades.length,
      buyCount,
      shortCount,
      totalInvested,
      totalGrossProfit,
      totalCommission,
      totalNetProfit: totalGrossProfit - totalCommission,
      unusedCash: investmentAmount - totalInvested,
    };
  }, [pendingTrades, investmentAmount, commissionPct]);

  const summary = calculateSummary();

  useEffect(() => {
    // Fetch data on mount
    Promise.all([
      fetchPendingTrades(),
      fetchZerodhaStatus()
    ]);
  }, []); // Empty dependency array - only run once on mount

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out");
  };

  // Format currency
  const formatCurrency = (value, decimals = 2) => {
    if (value === null || value === undefined) return "—";
    return `₹${Number(value).toLocaleString("en-IN", { maximumFractionDigits: decimals })}`;
  };

  // Format percentage
  const formatPct = (value) => {
    if (value === null || value === undefined) return "—";
    const sign = value >= 0 ? "+" : "";
    return `${sign}${value.toFixed(2)}%`;
  };

  // Calculate evaluation date (2 trading days after trade date)
  const getEvaluationDate = (tradeDate) => {
    if (!tradeDate) return "—";
    const date = new Date(tradeDate);
    let daysAdded = 0;
    while (daysAdded < 2) {
      date.setDate(date.getDate() + 1);
      // Skip weekends (0 = Sunday, 6 = Saturday)
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        daysAdded++;
      }
    }
    return date.toISOString().split('T')[0];
  };

  // Format datetime for display
  const formatDateTime = (isoString) => {
    if (!isoString) return "—";
    const date = new Date(isoString);
    return date.toLocaleString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Kolkata'
    }) + ' IST';
  };

  // Format date only (for Actual Date column)
  const formatDate = (isoString) => {
    if (!isoString) return "—";
    const date = new Date(isoString);
    return date.toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      timeZone: 'Asia/Kolkata'
    });
  };

  // Format time only (for Actual Date column)
  const formatTime = (isoString) => {
    if (!isoString) return "—";
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-IN', { 
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'Asia/Kolkata'
    }) + ' IST';
  };

  // Download pending trades as CSV
  const downloadPendingTradesCSV = () => {
    console.log('Export CSV clicked, pendingTrades:', pendingTrades.length);
    
    if (pendingTrades.length === 0) {
      toast.error("No pending trades to download");
      return;
    }

    try {
      const headers = [
        "Confidence Score", "Stock Name", "Entry Price", "Trade Date", "Industry", "Trading Signal", 
        "Review Date", "Target Price (2-Day)", "Number of Shares", "Investment Amount", 
        "Gross Profit / Loss", "Brokerage Fee", "Net Profit / Loss"
      ];
      
      const allocationPerStock = investmentAmount / pendingTrades.length;
      
      const rows = pendingTrades.map(trade => {
        const startPrice = trade.start_price;
        const predictedPrice = trade.predicted_price;  // Keep null as null
        
        // Skip if no valid start price
        if (!startPrice || startPrice === 0) {
          return null;
        }
        
        // Use strategy from database - don't recalculate
        const displayStrategy = trade.strategy || 'BUY';
        
        // Calculate shares
        const shares = Math.floor(allocationPerStock / startPrice);
        const invested = shares * startPrice;
        
        // Gross Profit: Only calculate if predicted price exists
        // BUY = (predicted - start) × shares, SHORT = (start - predicted) × shares
        let grossProfit = 0;
        if (predictedPrice !== null && predictedPrice !== undefined) {
          const priceDiff = displayStrategy === 'BUY' 
            ? predictedPrice - startPrice
            : startPrice - predictedPrice;
          grossProfit = priceDiff * shares;
        }
        const commission = invested * (commissionPct / 100);
        const netProfit = grossProfit - commission;

        // Format trade date with time
        const tradeDateTime = trade.trade_date + (trade.generation_time 
          ? ' ' + new Date(trade.generation_time).toLocaleTimeString('en-IN', { 
              hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' 
            }) + ' IST'
          : '');

        return [
          (trade.probability_score !== null && trade.probability_score !== undefined) ? trade.probability_score.toFixed(1) : '',
          trade.symbol?.replace('.NS', '') || '',
          startPrice.toFixed(2),
          tradeDateTime || '',
          trade.sector || trade.name || '',
          displayStrategy,
          trade.evaluation_date || '',
          predictedPrice !== null && predictedPrice !== undefined ? predictedPrice.toFixed(2) : 'N/A',
          shares,
          invested.toFixed(2),
          grossProfit.toFixed(2),
          commission.toFixed(2),
          netProfit.toFixed(2)
        ];
      }).filter(row => row !== null);  // Remove null rows (stocks without start_price)

      const csvContent = [
        `# Pending Trades - Awaiting Evaluation`,
        `# Downloaded: ${new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST`,
        `# Total Investment: ${investmentAmount}`,
        `# Commission: ${commissionPct}%`,
        '',
        headers.join(","),
        ...rows.map(row => row.map(cell => `"${cell || ''}"`).join(","))
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `pending_trades_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${rows.length} pending trades`);
    } catch (error) {
      console.error('CSV export error:', error);
      toast.error('Failed to export CSV: ' + error.message);
    }
  };

  const clearAllDashboard = async () => {
    if (!window.confirm("⚠️ This will move ALL stocks from the dashboard to history. Continue?")) {
      return;
    }
    
    try {
      setPendingLoading(true);
      toast.info("Clearing dashboard...");
      
      const res = await axios.post(`${API}/ai/clear-all-dashboard`);
      
      toast.success(res.data.message);
      
      // Refresh the trades list
      await fetchPendingTrades();
      
    } catch (err) {
      console.error("Error clearing dashboard:", err);
      toast.error(err.response?.data?.detail || "Failed to clear dashboard");
    } finally {
      setPendingLoading(false);
    }
  };


  // Render global market indicator card
  const IndicatorCard = ({ title, data, icon: Icon }) => {
    if (!data) return (
      <Card className="p-3 bg-[hsl(var(--card))] border-[hsl(var(--border))]">
        <Skeleton className="h-16" />
      </Card>
    );
    
    const isPositive = data.change_pct >= 0;
    
    return (
      <Card data-testid={`indicator-${title.toLowerCase().replace(/\s+/g, '-')}`} className="p-3 bg-[hsl(var(--card))] border-[hsl(var(--border))]">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-muted-foreground uppercase tracking-wide">{title}</span>
          {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-semibold font-mono">{data.price?.toLocaleString() || "—"}</span>
          <span className={`text-sm font-mono ${isPositive ? "text-emerald-400" : "text-red-400"}`}>
            {formatPct(data.change_pct)}
          </span>
        </div>
      </Card>
    );
  };

  return (
    <TooltipProvider>
      <div data-testid="ai-dashboard" className="min-h-screen bg-[hsl(var(--background))]">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-[hsl(var(--surface))] border-b border-[hsl(var(--border))] px-4 py-3">
          <div className="max-w-[1600px] mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Brain className="h-6 w-6 text-[hsl(var(--primary))]" />
              <h1 className="text-xl font-semibold">StockSense AI</h1>
              <Badge variant="outline" className="text-xs bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30">
                13-Module System
              </Badge>
              
              {/* Navigation Links - Hide when in iframe */}
              {window.self === window.top && (
              <nav className="flex items-center gap-1 ml-6 pl-6 border-l border-[hsl(var(--border))]">
                <Link to="/ai-history">
                  <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground hover:text-foreground">
                    <History className="h-4 w-4" />
                    History
                  </Button>
                </Link>
                <Link to="/performance">
                  <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground hover:text-foreground">
                    <LineChart className="h-4 w-4" />
                    Analytics
                  </Button>
                </Link>
                {/* Admin only links */}
                {user?.role !== 'guest' && (
                  <>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setAddStockOpen(true)}
                      className="gap-2 text-muted-foreground hover:text-foreground"
                    >
                      <Plus className="h-4 w-4" />
                      Add Stock
                    </Button>
                    <Link to="/batch-jobs">
                      <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground hover:text-foreground">
                        <Settings className="h-4 w-4" />
                        Jobs
                      </Button>
                    </Link>
                    <Link to="/machine-learning">
                      <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground hover:text-foreground">
                        <Brain className="h-4 w-4" />
                        ML
                      </Button>
                    </Link>
                  </>
                )}
              </nav>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              {/* Admin-only buttons - hidden for guest users and when in iframe */}
              {user?.role !== 'guest' && window.self === window.top && (
                <>
                  <Button 
                    data-testid="run-pipeline-btn"
                    onClick={runPipeline}
                    disabled={runningPipeline}
                    size="sm"
                    className="gap-2 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90"
                  >
                    {runningPipeline ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Play className="h-4 w-4" />
                    )}
                    Run AI Pipeline
                  </Button>
                  
                  {/* Zerodha Integration Status */}
                  <div className="flex items-center gap-2">
                    {zerodhaStatus?.authenticated ? (
                      <Badge 
                        variant="outline" 
                        className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 gap-1"
                      >
                        <Activity className="h-3 w-3" />
                        Zerodha Live
                      </Badge>
                    ) : (
                      <Button
                        data-testid="zerodha-login-btn"
                        variant="outline"
                        size="sm"
                        onClick={handleZerodhaLogin}
                        disabled={zerodhaLoading}
                        className="gap-2 border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                      >
                        {zerodhaLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Zap className="h-4 w-4" />
                        )}
                        Connect Zerodha
                      </Button>
                    )}
                  </div>
                  
                  {/* Deployment Status & Sync Helper */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="gap-2 border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                      >
                        <Cloud className="h-4 w-4" />
                        Deploy Status
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72" align="end">
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Cloud className="h-5 w-5 text-blue-400" />
                          <h4 className="font-semibold">Deployment Status</h4>
                        </div>
                        
                        {/* Status Indicator */}
                        <div className="bg-[hsl(var(--surface))] rounded-lg p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">Status</span>
                            <Badge variant="outline" className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                              Running
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">Environment</span>
                            <span className="text-sm">Preview</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">Last Deployed</span>
                            <span className="text-sm font-medium">{new Date().toLocaleDateString()} {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                          </div>
                        </div>
                        
                        {/* Action Button */}
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="w-full gap-1"
                          onClick={() => window.open('https://www.emergentagent.com', '_blank')}
                        >
                          <ExternalLink className="h-3 w-3" />
                          Open Emergent Dashboard
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </>
              )}
              
              <div className="flex items-center gap-2 ml-4 pl-4 border-l border-[hsl(var(--border))]">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{user?.name || user?.username}</span>
                <Button variant="ghost" size="icon" onClick={handleLogout}>
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-[1600px] mx-auto px-4 py-6 space-y-6">
          
          {/* ==================== GLOBAL MARKET PANEL ==================== */}
          {/* ==================== PENDING TRADES (Awaiting Evaluation) ==================== */}
          <section data-testid="pending-trades-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Zap className="h-5 w-5 text-[hsl(var(--accent))]" />
                Pending Trades
                <Badge variant="outline" className="text-xs">Awaiting Evaluation</Badge>
              </h2>
              
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-emerald-400">{summary.buyCount} BUY</span>
                  <span className="text-red-400">{summary.shortCount} SHORT</span>
                </div>
                
                {/* Column Selector */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2">
                      <SlidersHorizontal className="h-4 w-4" />
                      Columns
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64" align="end">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-sm">Toggle Columns</h4>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={resetColumnWidths}
                          className="text-xs h-7 px-2"
                        >
                          Reset Widths
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">Drag column borders to resize</p>
                      <div className="border-t pt-3 space-y-2">
                        {AVAILABLE_COLUMNS.map(col => (
                          <div key={col.id} className="flex items-center space-x-2">
                            <Checkbox 
                              id={`col-${col.id}`}
                              checked={isColumnVisible(col.id)}
                              onCheckedChange={() => toggleColumn(col.id)}
                            />
                            <label 
                              htmlFor={`col-${col.id}`}
                              className="text-sm cursor-pointer flex-1"
                            >
                              {col.label}
                            </label>
                            <span className="text-xs text-muted-foreground font-mono">
                              {columnWidths[col.id] || col.defaultWidth}px
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={fetchPendingTrades}
                  disabled={pendingLoading}
                >
                  <RefreshCw className={`h-4 w-4 ${pendingLoading ? "animate-spin" : ""}`} />
                </Button>
                {pendingTrades.length > 0 && (
                  <>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={downloadPendingTradesCSV}
                      className="gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Export CSV
                    </Button>
                    <Button 
                      variant="destructive" 
                      size="sm" 
                      onClick={clearAllDashboard}
                      disabled={pendingLoading}
                      className="gap-2"
                    >
                      <History className="h-4 w-4" />
                      Clear All
                    </Button>
                  </>
                )}
              </div>
            </div>

            {pendingLoading ? (
              <Card className="p-6">
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              </Card>
            ) : pendingTrades.length === 0 ? (
              <Card className="p-8 text-center">
                <Brain className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Pending Trades</h3>
                <p className="text-muted-foreground mb-4">
                  Run the AI Pipeline to generate today's trade recommendations.
                </p>
                <Button onClick={runPipeline} disabled={runningPipeline}>
                  {runningPipeline ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Play className="h-4 w-4 mr-2" />
                  )}
                  Generate Trade Plan
                </Button>
              </Card>
            ) : (
              <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" style={{ minWidth: '1500px', tableLayout: 'fixed' }}>
                    <thead className="bg-[hsl(var(--surface))]">
                      <tr>
                        {isColumnVisible('probability') && (
                          <th 
                            className="text-right p-3 font-medium whitespace-nowrap relative group" 
                            style={{ width: columnWidths['probability'] || 120, minWidth: 100 }}
                          >
                            Confidence Score
                            <div 
                              className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-transparent hover:bg-[hsl(var(--primary))] transition-colors"
                              onMouseDown={(e) => handleResizeStart(e, 'probability')}
                              data-testid="resize-handle-probability"
                            />
                          </th>
                        )}
                        {isColumnVisible('stock') && (
                          <th 
                            className="text-left p-3 font-medium whitespace-nowrap relative group" 
                            style={{ width: columnWidths['stock'] || 160, minWidth: 120 }}
                          >
                            Stock Name
                            <div 
                              className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-transparent hover:bg-[hsl(var(--primary))] transition-colors"
                              onMouseDown={(e) => handleResizeStart(e, 'stock')}
                              data-testid="resize-handle-stock"
                            />
                          </th>
                        )}
                        {isColumnVisible('ranking_score') && (
                          <th 
                            className="text-right p-3 font-medium whitespace-nowrap relative group" 
                            style={{ width: columnWidths['ranking_score'] || 100, minWidth: 80 }}
                          >
                            Rank Score
                            <div 
                              className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-transparent hover:bg-[hsl(var(--primary))] transition-colors"
                              onMouseDown={(e) => handleResizeStart(e, 'ranking_score')}
                              data-testid="resize-handle-ranking-score"
                            />
                          </th>
                        )}
                        {isColumnVisible('start_price') && (
                          <th 
                            className="text-right p-3 font-medium whitespace-nowrap relative group" 
                            style={{ width: columnWidths['start_price'] || 110, minWidth: 90 }}
                          >
                            Entry Price
                            <div 
                              className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-transparent hover:bg-[hsl(var(--primary))] transition-colors"
                              onMouseDown={(e) => handleResizeStart(e, 'start_price')}
                              data-testid="resize-handle-start-price"
                            />
                          </th>
                        )}
                        {isColumnVisible('actual_price') && (
                          <th 
                            className="text-right p-3 font-medium whitespace-nowrap relative group" 
                            style={{ width: columnWidths['actual_price'] || 110, minWidth: 90 }}
                          >
                            Actual Price
                            <div 
                              className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-transparent hover:bg-[hsl(var(--primary))] transition-colors"
                              onMouseDown={(e) => handleResizeStart(e, 'actual_price')}
                              data-testid="resize-handle-actual-price"
                            />
                          </th>
                        )}
                        {isColumnVisible('actual_date') && (
                          <th 
                            className="text-left p-3 font-medium whitespace-nowrap relative group" 
                            style={{ width: columnWidths['actual_date'] || 180, minWidth: 140 }}
                          >
                            Actual Date
                            <div 
                              className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-transparent hover:bg-[hsl(var(--primary))] transition-colors"
                              onMouseDown={(e) => handleResizeStart(e, 'actual_date')}
                              data-testid="resize-handle-actual-date"
                            />
                          </th>
                        )}
                        {isColumnVisible('trade_datetime') && (
                          <th 
                            className="text-left p-3 font-medium whitespace-nowrap relative group" 
                            style={{ width: columnWidths['trade_datetime'] || 120, minWidth: 100 }}
                          >
                            Trade Date
                            <div 
                              className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-transparent hover:bg-[hsl(var(--primary))] transition-colors"
                              onMouseDown={(e) => handleResizeStart(e, 'trade_datetime')}
                              data-testid="resize-handle-trade-datetime"
                            />
                          </th>
                        )}
                        {isColumnVisible('sector') && (
                          <th 
                            className="text-left p-3 font-medium whitespace-nowrap relative group" 
                            style={{ width: columnWidths['sector'] || 140, minWidth: 100 }}
                          >
                            Industry
                            <div 
                              className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-transparent hover:bg-[hsl(var(--primary))] transition-colors"
                              onMouseDown={(e) => handleResizeStart(e, 'sector')}
                              data-testid="resize-handle-sector"
                            />
                          </th>
                        )}
                        {isColumnVisible('strategy') && (
                          <th 
                            className="text-center p-3 font-medium whitespace-nowrap relative group" 
                            style={{ width: columnWidths['strategy'] || 100, minWidth: 80 }}
                          >
                            Trading Signal
                            <div 
                              className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-transparent hover:bg-[hsl(var(--primary))] transition-colors"
                              onMouseDown={(e) => handleResizeStart(e, 'strategy')}
                              data-testid="resize-handle-strategy"
                            />
                          </th>
                        )}
                        {isColumnVisible('eval_date') && (
                          <th 
                            className="text-left p-3 font-medium whitespace-nowrap relative group" 
                            style={{ width: columnWidths['eval_date'] || 120, minWidth: 100 }}
                          >
                            Review Date
                            <div 
                              className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-transparent hover:bg-[hsl(var(--primary))] transition-colors"
                              onMouseDown={(e) => handleResizeStart(e, 'eval_date')}
                              data-testid="resize-handle-eval-date"
                            />
                          </th>
                        )}
                        {isColumnVisible('predicted_price') && (
                          <th 
                            className="text-right p-3 font-medium whitespace-nowrap relative group" 
                            style={{ width: columnWidths['predicted_price'] || 130, minWidth: 100 }}
                          >
                            Target Price (2-Day)
                            <div 
                              className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-transparent hover:bg-[hsl(var(--primary))] transition-colors"
                              onMouseDown={(e) => handleResizeStart(e, 'predicted_price')}
                              data-testid="resize-handle-predicted-price"
                            />
                          </th>
                        )}
                        {isColumnVisible('shares') && (
                          <th 
                            className="text-right p-3 font-medium whitespace-nowrap relative group" 
                            style={{ width: columnWidths['shares'] || 90, minWidth: 70 }}
                          >
                            Number of Shares
                            <div 
                              className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-transparent hover:bg-[hsl(var(--primary))] transition-colors"
                              onMouseDown={(e) => handleResizeStart(e, 'shares')}
                              data-testid="resize-handle-shares"
                            />
                          </th>
                        )}
                        {isColumnVisible('investment') && (
                          <th 
                            className="text-right p-3 font-medium whitespace-nowrap relative group" 
                            style={{ width: columnWidths['investment'] || 120, minWidth: 100 }}
                          >
                            Investment Amount
                            <div 
                              className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-transparent hover:bg-[hsl(var(--primary))] transition-colors"
                              onMouseDown={(e) => handleResizeStart(e, 'investment')}
                              data-testid="resize-handle-investment"
                            />
                          </th>
                        )}
                        {isColumnVisible('gross_pl') && (
                          <th 
                            className="text-right p-3 font-medium whitespace-nowrap relative group" 
                            style={{ width: columnWidths['gross_pl'] || 110, minWidth: 90 }}
                          >
                            Gross Profit / Loss
                            <div 
                              className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-transparent hover:bg-[hsl(var(--primary))] transition-colors"
                              onMouseDown={(e) => handleResizeStart(e, 'gross_pl')}
                              data-testid="resize-handle-gross-pl"
                            />
                          </th>
                        )}
                        {isColumnVisible('commission') && (
                          <th 
                            className="text-right p-3 font-medium whitespace-nowrap relative group" 
                            style={{ width: columnWidths['commission'] || 110, minWidth: 90 }}
                          >
                            Brokerage Fee
                            <div 
                              className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-transparent hover:bg-[hsl(var(--primary))] transition-colors"
                              onMouseDown={(e) => handleResizeStart(e, 'commission')}
                              data-testid="resize-handle-commission"
                            />
                          </th>
                        )}
                        {isColumnVisible('net_pl') && (
                          <th 
                            className="text-right p-3 font-medium whitespace-nowrap relative group" 
                            style={{ width: columnWidths['net_pl'] || 110, minWidth: 90 }}
                          >
                            Net Profit / Loss
                            <div 
                              className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-transparent hover:bg-[hsl(var(--primary))] transition-colors"
                              onMouseDown={(e) => handleResizeStart(e, 'net_pl')}
                              data-testid="resize-handle-net-pl"
                            />
                          </th>
                        )}
                        {isColumnVisible('actions') && (
                          <th 
                            className="text-center p-3 font-medium whitespace-nowrap" 
                            style={{ width: columnWidths['actions'] || 100, minWidth: 80 }}
                          >
                            Actions
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {pendingTrades.map((trade, idx) => {
                        // Use strategy from database - don't recalculate
                        const startPrice = trade.start_price;
                        const predictedPrice = trade.predicted_price;  // Keep null as null
                        const displayStrategy = trade.strategy || 'BUY';
                        
                        // Skip if no valid start price
                        if (!startPrice || startPrice === 0) {
                          return null;
                        }
                        
                        // Calculate shares for ALL stocks (equal allocation)
                        const allocationPerStock = investmentAmount / pendingTrades.length;
                        const shares = Math.floor(allocationPerStock / startPrice);
                        const invested = shares * startPrice;
                        
                        // Gross Profit: Only calculate if predicted price exists
                        // BUY = (predicted - start) × shares, SHORT = (start - predicted) × shares
                        let grossProfit = 0;
                        if (predictedPrice !== null && predictedPrice !== undefined) {
                          const priceDiff = displayStrategy === 'BUY' 
                            ? predictedPrice - startPrice
                            : startPrice - predictedPrice;
                          grossProfit = priceDiff * shares;
                        }
                        const commission = invested * (commissionPct / 100);
                        const netProfit = grossProfit - commission;
                        
                        return (
                          <tr 
                            key={`${trade.trade_date}-${trade.symbol}-${idx}`} 
                            data-testid={`pending-row-${trade.symbol}`}
                            className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--surface))]/50 transition-colors"
                          >
                            {isColumnVisible('probability') && (
                              <td className="p-3 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <div className="w-10 h-2 bg-[hsl(var(--surface-2))] rounded-full overflow-hidden">
                                    <div 
                                      className={`h-full rounded-full ${
                                        trade.probability_score >= 70 ? 'bg-emerald-500' :
                                        trade.probability_score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                      }`}
                                      style={{ width: `${trade.probability_score || 0}%` }}
                                    />
                                  </div>
                                  <span className="font-mono text-xs">
                                    {(trade.probability_score !== null && trade.probability_score !== undefined) ? trade.probability_score.toFixed(0) : '—'}%
                                  </span>
                                </div>
                              </td>
                            )}
                            {isColumnVisible('stock') && (
                              <td className="p-3">
                                <div className="font-medium text-base">{trade.symbol?.replace('.NS', '').replace('.BO', '')}</div>
                                {trade.manually_added && <Badge variant="outline" className="text-[10px] mt-1">Manual</Badge>}
                              </td>
                            )}
                            {isColumnVisible('ranking_score') && (
                              <td className="p-3 text-right">
                                <span className="font-mono text-[hsl(var(--primary))] font-medium">
                                  {trade.ranking_score?.toFixed(1) || '—'}
                                </span>
                              </td>
                            )}
                            {isColumnVisible('start_price') && (
                              <td className="p-3 text-right font-mono">{formatCurrency(startPrice)}</td>
                            )}
                            {isColumnVisible('actual_price') && (
                              <td className="p-3 text-right font-mono">
                                {trade.actual_price ? (
                                  <span className={trade.actual_price >= startPrice ? 'text-emerald-400' : 'text-red-400'}>
                                    {formatCurrency(trade.actual_price)}
                                  </span>
                                ) : '—'}
                              </td>
                            )}
                            {isColumnVisible('actual_date') && (
                              <td className="p-3 font-mono text-xs">
                                {trade.price_updated_at ? (
                                  <div>
                                    <div>{formatDate(trade.price_updated_at)}</div>
                                    <div className="text-muted-foreground">{formatTime(trade.price_updated_at)}</div>
                                  </div>
                                ) : trade.target_hit_at ? (
                                  <div>
                                    <div>{formatDate(trade.target_hit_at)}</div>
                                    <div className="text-muted-foreground">{formatTime(trade.target_hit_at)}</div>
                                  </div>
                                ) : '—'}
                              </td>
                            )}
                            {isColumnVisible('trade_datetime') && (
                              <td className="p-3 font-mono text-xs">
                                <div>{trade.trade_date}</div>
                                <div className="text-muted-foreground">{formatDateTime(trade.generation_time)}</div>
                              </td>
                            )}
                            {isColumnVisible('sector') && (
                              <td className="p-3 text-xs text-muted-foreground">{trade.sector || trade.name || '—'}</td>
                            )}
                            {isColumnVisible('strategy') && (
                              <td className="p-3 text-center">
                                <Badge className={`font-semibold ${STRATEGY_COLORS[displayStrategy]}`}>
                                  {displayStrategy}
                                </Badge>
                              </td>
                            )}
                            {isColumnVisible('eval_date') && (
                              <td className="p-3 font-mono text-xs">
                                {trade.is_evaluated ? (
                                  <div>
                                    <div className="text-emerald-400">{trade.evaluated_at ? new Date(trade.evaluated_at).toLocaleDateString() : '—'}</div>
                                    <div className="text-muted-foreground">{trade.evaluated_at ? new Date(trade.evaluated_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ''}</div>
                                  </div>
                                ) : (
                                  <div className="text-[hsl(var(--primary))]">
                                    {trade.evaluation_date || '—'}
                                  </div>
                                )}
                              </td>
                            )}
                            {isColumnVisible('predicted_price') && (
                              <td className="p-3 text-right font-mono text-[hsl(var(--primary))]">{formatCurrency(predictedPrice)}</td>
                            )}
                            {isColumnVisible('shares') && (
                              <td className="p-3 text-right font-mono">{shares > 0 ? shares.toLocaleString() : '—'}</td>
                            )}
                            {isColumnVisible('investment') && (
                              <td className="p-3 text-right font-mono">{invested > 0 ? formatCurrency(invested, 0) : '—'}</td>
                            )}
                            {isColumnVisible('gross_pl') && (
                              <td className={`p-3 text-right font-mono ${trade.is_evaluated ? (grossProfit >= 0 ? 'text-emerald-400' : 'text-red-400') : 'text-muted-foreground'}`}>
                                {trade.is_evaluated ? formatCurrency(grossProfit) : '—'}
                              </td>
                            )}
                            {isColumnVisible('commission') && (
                              <td className="p-3 text-right font-mono text-muted-foreground">
                                {trade.is_evaluated ? formatCurrency(commission) : '—'}
                              </td>
                            )}
                            {isColumnVisible('net_pl') && (
                              <td className={`p-3 text-right font-mono ${trade.is_evaluated ? (netProfit >= 0 ? 'text-emerald-400' : 'text-red-400') : 'text-muted-foreground'}`}>
                                {trade.is_evaluated ? formatCurrency(netProfit) : '—'}
                              </td>
                            )}
                            {isColumnVisible('actions') && (
                              <td className="p-3">
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => evaluateStock(trade.symbol)}
                                    disabled={evaluatingStock === trade.symbol}
                                    className="h-7 px-2 text-xs"
                                    data-testid={`evaluate-btn-${trade.symbol}`}
                                  >
                                    {evaluatingStock === trade.symbol ? (
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                      <>
                                        <Target className="h-3 w-3 mr-1" />
                                        Evaluate
                                      </>
                                    )}
                                  </Button>
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => deleteStock(trade)}
                                    disabled={deletingStock === trade.symbol}
                                    className="h-7 px-2 text-xs"
                                    data-testid={`delete-btn-${trade.symbol}`}
                                  >
                                    {deletingStock === trade.symbol ? (
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                      <Trash2 className="h-3 w-3" />
                                    )}
                                  </Button>
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </section>

          {/* ==================== INVESTMENT CALCULATOR ==================== */}
          <section data-testid="investment-calculator">
            <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
              <Target className="h-5 w-5 text-[hsl(var(--primary))]" />
              Investment Summary
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              {/* Input Card */}
              <Card className="p-4">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="investment">Total Investment (₹)</Label>
                    <Input
                      id="investment"
                      type="number"
                      value={investmentAmount}
                      onChange={(e) => setInvestmentAmount(Number(e.target.value))}
                      className="mt-1 font-mono"
                    />
                  </div>
                  <div>
                    <Label htmlFor="commission">Commission (%)</Label>
                    <Input
                      id="commission"
                      type="number"
                      step="0.1"
                      value={commissionPct}
                      onChange={(e) => setCommissionPct(Number(e.target.value))}
                      className="mt-1 font-mono"
                    />
                  </div>
                </div>
              </Card>
              
              {/* Allocation Summary */}
              <Card className="p-4">
                <h3 className="text-sm font-medium text-muted-foreground mb-3">Allocation Summary</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Pending</span>
                    <span className="font-mono font-medium">{summary.totalTrades}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tradeable</span>
                    <span className="font-mono font-medium text-emerald-400">{summary.tradeableCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Invested</span>
                    <span className="font-mono font-medium">{formatCurrency(summary.totalInvested, 0)}</span>
                  </div>
                </div>
              </Card>
              
              {/* Gross Profit */}
              <Card className="p-4">
                <h3 className="text-sm font-medium text-muted-foreground mb-3">Gross Profit (Projected)</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Projected Gross P/L</span>
                    <span className={`font-mono font-medium ${summary.totalGrossProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {formatCurrency(summary.totalGrossProfit, 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Gross Return %</span>
                    <span className={`font-mono font-medium ${summary.totalGrossProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {formatPct(summary.totalInvested > 0 ? (summary.totalGrossProfit / summary.totalInvested * 100) : 0)}
                    </span>
                  </div>
                </div>
              </Card>
              
              {/* Net Profit */}
              <Card className="p-4">
                <h3 className="text-sm font-medium text-muted-foreground mb-3">Net Profit (Projected)</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Commission ({commissionPct}%)</span>
                    <span className="font-mono font-medium text-red-400">
                      -{formatCurrency(summary.totalInvested * (commissionPct / 100), 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Net P/L</span>
                    <span className={`font-mono font-medium ${summary.totalNetProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {formatCurrency(summary.totalNetProfit, 0)}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-[hsl(var(--border))] pt-3">
                    <span className="text-muted-foreground">Net Return %</span>
                    <span className={`font-mono font-medium ${summary.totalNetProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {formatPct(summary.totalInvested > 0 ? (summary.totalNetProfit / summary.totalInvested * 100) : 0)}
                    </span>
                  </div>
                </div>
              </Card>
            </div>
          </section>

          {/* Disclaimer */}
          <div data-testid="disclaimer" className="text-xs text-muted-foreground bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] rounded-md p-3">
            Market data is provided for informational purposes only. StockSense AI does not provide investment advice. 
            Predictions are generated using AI analysis of US market signals, India news sentiment, and global indicators. 
            Past performance does not guarantee future results.
          </div>
        </main>

        {/* Add Stock Dialog - Admin only */}
        <Dialog open={addStockOpen} onOpenChange={setAddStockOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Brain className="h-5 w-5 text-[hsl(var(--primary))]" />
                Add Stock with Full Evaluation
              </DialogTitle>
              <DialogDescription>
                Add a stock and immediately run all 14 analysis modules to generate predictions.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div>
                <Label htmlFor="stock-symbol">Stock Symbol (NSE/BSE)</Label>
                <Input
                  id="stock-symbol"
                  placeholder="e.g., RELIANCE, INFY, HDFCBANK"
                  value={newStockSymbol}
                  onChange={(e) => setNewStockSymbol(e.target.value.toUpperCase())}
                  className="mt-2 font-mono"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Enter the symbol without .NS/.BO suffix - it will be added automatically
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddStockOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddStock} disabled={addingStock || !newStockSymbol.trim()}>
                {addingStock ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Evaluating...
                  </>
                ) : (
                  <>
                    <Zap className="h-4 w-4 mr-2" />
                    Add & Evaluate
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
